import { CmsMedia } from '@shared/models/cms.model';
import { LandingSeo, MetaSocial } from '@core/cms/landing.cms';
import { PageSeo, PageSeoSocial, SocialNetwork } from '@core/seo/seo.model';

/** CMS `socialNetwork` string → {@link SocialNetwork}; unknown/absent networks are dropped. */
const SOCIAL_NETWORK_BY_NAME: Readonly<Record<string, SocialNetwork>> = {
    Facebook: SocialNetwork.Facebook,
    Twitter: SocialNetwork.Twitter,
};

/**
 * Resolves a CMS media URL to an absolute one. Strapi returns upload URLs relative to its own host
 * (e.g. `/uploads/foo.png`); already-absolute URLs (`http(s)://…`) are returned untouched.
 */
export function resolveCmsMediaUrl(
    media: CmsMedia | null | undefined,
    cmsBaseUrl: string,
): string | null {
    const url: string | null = media?.url ?? null;

    if (!url) {
        return null;
    }

    if (/^https?:\/\//i.test(url)) {
        return url;
    }

    return `${cmsBaseUrl}${url}`;
}

/** Maps a CMS `shared.meta-social` entry to a normalized {@link PageSeoSocial}. */
function toPageSeoSocial(meta: MetaSocial, cmsBaseUrl: string): PageSeoSocial | null {
    const network: SocialNetwork | undefined = meta.socialNetwork
        ? SOCIAL_NETWORK_BY_NAME[meta.socialNetwork]
        : undefined;

    if (!network) {
        return null;
    }

    return {
        network,
        title: meta.title ?? null,
        description: meta.description ?? null,
        imageUrl: resolveCmsMediaUrl(meta.image, cmsBaseUrl),
    };
}

/**
 * Maps the CMS `shared.seo` component ({@link LandingSeo}) onto the source-agnostic {@link PageSeo}
 * the service consumes. Pure given `cmsBaseUrl`, so it is trivially unit-testable; media URLs are
 * resolved here so the service never needs to know about the CMS host.
 */
export function cmsSeoToPageSeo(seo: LandingSeo, cmsBaseUrl: string): PageSeo {
    const social: ReadonlyArray<PageSeoSocial> = seo.metaSocial
        .map((meta: MetaSocial): PageSeoSocial | null => toPageSeoSocial(meta, cmsBaseUrl))
        .filter((entry: PageSeoSocial | null): entry is PageSeoSocial => entry !== null);

    return {
        title: seo.metaTitle ?? null,
        description: seo.metaDescription ?? null,
        keywords: seo.keywords ?? null,
        robots: seo.metaRobots ?? null,
        viewport: seo.metaViewport ?? null,
        canonicalUrl: seo.canonicalURL ?? null,
        imageUrl: resolveCmsMediaUrl(seo.metaImage, cmsBaseUrl),
        structuredData: seo.structuredData ?? null,
        social,
    };
}
